/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import NewsletterCapture from "@/components/NewsletterCapture";
import ShareLinkedIn from "@/components/ShareLinkedIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const renderContent = (content) => {
  // Simple renderer: paragraphs split by \n\n; ## headings become h2; numeric lists become ordered list-ish
  const blocks = content.split(/\n\n+/);
  return blocks.map((b, i) => {
    if (b.startsWith("## ")) {
      return <h2 key={i}>{b.replace(/^##\s+/, "")}</h2>;
    }
    if (/^\d\.\s/.test(b)) {
      const items = b.split(/\n/).map((line) => line.replace(/^\d\.\s+/, ""));
      return (
        <ol key={i} className="list-decimal pl-6 space-y-2 my-6 text-muted-foreground leading-relaxed">
          {items.map((it, j) => (
            <li key={j}>{it}</li>
          ))}
        </ol>
      );
    }
    if (/^- /.test(b)) {
      const items = b.split(/\n/).map((line) => line.replace(/^-\s+/, ""));
      return (
        <ul key={i} className="list-disc pl-6 space-y-2 my-6 text-muted-foreground leading-relaxed">
          {items.map((it, j) => (
            <li key={j}>{it}</li>
          ))}
        </ul>
      );
    }
    return <p key={i}>{b}</p>;
  });
};

const InsightDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPost = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(r.data);
    } catch (e) {
      setError(e?.response?.status === 404 ? "Article not found" : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  if (loading) {
    return (
      <div className="pt-[160px] max-w-editorial mx-auto px-6 md:px-10 lg:px-12 font-mono text-[12px] text-muted" data-testid="insight-loading">
        Loading…
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="pt-[160px] pb-32 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <Link to="/insights" className="inline-flex items-center gap-2 text-[13px] font-mono text-muted hover:text-ink mb-12">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to insights
        </Link>
        <h1 className="font-display font-bold text-[2rem]" data-testid="insight-error">{error || "Not found"}</h1>
      </div>
    );
  }

  return (
    <>
      <section className="pt-[140px] pb-12 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <Link to="/insights" className="inline-flex items-center gap-2 text-[13px] font-mono tracking-wide text-muted hover:text-ink mb-12" data-testid="insight-back">
          <ArrowLeft className="h-3.5 w-3.5" /> All insights
        </Link>

        <div className="flex items-center gap-3 mb-8 font-mono text-[11px] tracking-overline uppercase text-muted">
          <span>{post.category}</span>
          <span className="w-1 h-1 bg-muted rounded-full" />
          <span>{formatDate(post.published_at)}</span>
          <span className="w-1 h-1 bg-muted rounded-full" />
          <span>{post.read_minutes} min read</span>
        </div>

        <h1 className="font-display font-bold text-[2.5rem] md:text-[4.5rem] leading-[0.98] tracking-tightest max-w-4xl mb-10">
          {post.title}
        </h1>

        <div className="aspect-[21/9] rounded-md overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="pb-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <div className="font-mono text-[11px] tracking-overline uppercase text-muted mb-2">By</div>
              <div className="font-display font-semibold text-[15px]">{post.author}</div>
            </div>
          </div>
          <article className="lg:col-span-8 prose-editorial text-[17px]">
            <p className="text-[20px] text-ink/80 leading-relaxed mb-8 font-medium font-serif italic">{post.excerpt}</p>
            {renderContent(post.content)}

            <div className="mt-12 pt-10 border-t border-black/8">
              <ShareLinkedIn slug={post.slug} title={post.title} />
            </div>

            <div className="mt-16">
              <NewsletterCapture source={`article:${post.slug}`} compact />
            </div>
          </article>
        </div>
      </section>

      <section className="py-24 bg-paper-warm">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h3 className="font-display font-bold text-[2rem] md:text-[2.5rem] tracking-tightest mb-6">
            Need a hand with this on your estate?
          </h3>
          <Link to="/contact" data-testid="insight-cta-contact" className="inline-flex items-center gap-2 px-7 py-4 bg-brand text-white font-semibold text-[14px] rounded-sm hover:bg-accent transition-colors">
            Talk to our team <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default InsightDetail;
